/**
 * Bar chart component built with D3.js v7.
 *
 * Supports vertical and horizontal layouts, stacked bars,
 * animated entrance, hover tooltips, and responsive resizing.
 */

import { useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import type { BarChartConfig } from "../../types/index.js";
import {
	SERIES_COLOURS,
	positionTooltip,
	formatNumber,
	useResizeObserver,
} from "./chartUtils.js";
import { useRenderLoopGuard } from "../../hooks/useRenderLoopGuard.js";

interface BarChartViewProps {
	config: BarChartConfig;
	isModal?: boolean;
}

const MARGINS = { top: 20, right: 20, bottom: 40, left: 50 };

export default function BarChartView({ config, isModal = false }: BarChartViewProps) {
	useRenderLoopGuard("BarChartView");
	const svgRef = useRef<SVGSVGElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const { width: containerWidth, height: containerHeight } = useResizeObserver(containerRef);

	const { data, series, xDataKey, xAxisLabel, yAxisLabel, layout = "vertical", stacked = false } = config;

	const renderChart = useCallback(() => {
		if (!svgRef.current || !data || data.length === 0 || containerWidth === 0) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		const isHorizontal = layout === "horizontal";
		// Use container height only when explicitly in modal context (isModal=true).
		// For inline charts the container height is content-driven, so using it
		// would create a feedback loop: SVG grows → container grows → SVG grows…
		const defaultHeight = isHorizontal
			? Math.max(300, data.length * 32 + MARGINS.top + MARGINS.bottom)
			: 320;
		const effectiveHeight = isModal ? containerHeight : 0;
		const height = effectiveHeight > 0 ? Math.min(Math.max(defaultHeight, effectiveHeight), 600) : defaultHeight;
		const width = containerWidth;
		const innerW = width - MARGINS.left - MARGINS.right;
		const innerH = height - MARGINS.top - MARGINS.bottom;

		svg
			.attr("width", width)
			.attr("height", height)           // intrinsic size for SVG export
			.style("height", `${height}px`)   // CSS override: beats initial height:0 on SVG element
			.attr("role", "img");

		const g = svg.append("g").attr("transform", `translate(${MARGINS.left},${MARGINS.top})`);

		// Colour scale per series
		const colourScale = (i: number) =>
			(series[i]?.colour) || SERIES_COLOURS[i % SERIES_COLOURS.length];

		// Keys for stack
		const keys = series.map((s) => s.dataKey);

		// -----------------------------------------------------------------------
		// Vertical layout
		// -----------------------------------------------------------------------
		if (!isHorizontal) {
			const xScale = d3
				.scaleBand()
				.domain(data.map((d) => String(d[xDataKey])))
				.range([0, innerW])
				.padding(0.25);

			const yMax = stacked
				? d3.max(data, (d) => keys.reduce((acc, k) => acc + (Number(d[k]) || 0), 0)) ?? 0
				: d3.max(data, (d) => d3.max(keys, (k) => Number(d[k]) || 0) ?? 0) ?? 0;

			const yScale = d3.scaleLinear().domain([0, yMax * 1.1]).nice().range([innerH, 0]);

			// Grid lines
			g.append("g")
				.attr("class", "chart-grid")
				.call(
					d3.axisLeft(yScale)
						.tickSize(-innerW)
						.tickFormat(() => "")
						.ticks(5)
				)
				.select(".domain")
				.remove();

			g.selectAll(".chart-grid .tick line")
				.attr("stroke", "#e5e7eb")
				.attr("stroke-dasharray", "3,3");

			// X axis
			g.append("g")
				.attr("transform", `translate(0,${innerH})`)
				.call(d3.axisBottom(xScale).tickSizeOuter(0))
				.selectAll("text")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "11px")
				.attr("fill", "#6b7280");

			// Y axis
			g.append("g")
				.call(d3.axisLeft(yScale).ticks(5).tickFormat((v) => formatNumber(Number(v))))
				.selectAll("text")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "11px")
				.attr("fill", "#6b7280");

			// Remove domain lines
			g.selectAll(".domain").attr("stroke", "#e5e7eb");

			// Axis labels
			if (xAxisLabel) {
				g.append("text")
					.attr("x", innerW / 2)
					.attr("y", innerH + 36)
					.attr("text-anchor", "middle")
					.attr("font-family", "var(--font-body)")
					.attr("font-size", "12px")
					.attr("fill", "#6b7280")
					.text(xAxisLabel);
			}

			if (yAxisLabel) {
				g.append("text")
					.attr("transform", "rotate(-90)")
					.attr("x", -innerH / 2)
					.attr("y", -38)
					.attr("text-anchor", "middle")
					.attr("font-family", "var(--font-body)")
					.attr("font-size", "12px")
					.attr("fill", "#6b7280")
					.text(yAxisLabel);
			}

			if (stacked) {
				// Stacked bars
				const stack = d3.stack<Record<string, string | number>>().keys(keys);
				const stackedData = stack(data);

				stackedData.forEach((layer, i) => {
					const seriesName = series[i]?.name ?? keys[i];
					const colour = colourScale(i);

					g.selectAll(`.bar-layer-${i}`)
						.data(layer)
						.join("rect")
						.attr("class", `bar-layer-${i}`)
						.attr("x", (d) => xScale(String((d.data as Record<string, unknown>)[xDataKey])) ?? 0)
						.attr("width", xScale.bandwidth())
						.attr("fill", colour)
						.attr("rx", i === stackedData.length - 1 ? 3 : 0)
						// Animate from bottom
						.attr("y", innerH)
						.attr("height", 0)
						.on("mouseenter", function (event: MouseEvent, d) {
							const value = d[1] - d[0];
							if (tooltipRef.current) {
								tooltipRef.current.innerHTML = `<strong>${seriesName}</strong><br/>${formatNumber(value)}`;
								tooltipRef.current.style.opacity = "1";
								positionTooltip(tooltipRef.current, event);
							}
							d3.select(this).attr("opacity", 0.8);
						})
						.on("mousemove", function (event: MouseEvent) {
							if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
						})
						.on("mouseleave", function () {
							if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
							d3.select(this).attr("opacity", 1);
						})
						.transition()
						.duration(600)
						.delay((_, j) => j * 20)
						.ease(d3.easeCubicOut)
						.attr("y", (d) => yScale(d[1]))
						.attr("height", (d) => Math.max(0, yScale(d[0]) - yScale(d[1])));
				});
			} else {
				// Grouped bars
				const xGroupScale = d3
					.scaleBand()
					.domain(keys)
					.range([0, xScale.bandwidth()])
					.padding(0.05);

				series.forEach((s, i) => {
					const colour = colourScale(i);

					g.selectAll(`.bar-group-${i}`)
						.data(data)
						.join("rect")
						.attr("class", `bar-group-${i}`)
						.attr("x", (d) => (xScale(String(d[xDataKey])) ?? 0) + (xGroupScale(s.dataKey) ?? 0))
						.attr("width", xGroupScale.bandwidth())
						.attr("fill", colour)
						.attr("rx", 3)
						// Animate from bottom
						.attr("y", innerH)
						.attr("height", 0)
						.on("mouseenter", function (event: MouseEvent, d) {
							const value = Number(d[s.dataKey]) || 0;
							if (tooltipRef.current) {
								tooltipRef.current.innerHTML = `<strong>${s.name}</strong><br/>${formatNumber(value)}`;
								tooltipRef.current.style.opacity = "1";
								positionTooltip(tooltipRef.current, event);
							}
							d3.select(this).attr("opacity", 0.8);
						})
						.on("mousemove", function (event: MouseEvent) {
							if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
						})
						.on("mouseleave", function () {
							if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
							d3.select(this).attr("opacity", 1);
						})
						.transition()
						.duration(600)
						.delay((_, j) => j * 20)
						.ease(d3.easeCubicOut)
						.attr("y", (d) => yScale(Number(d[s.dataKey]) || 0))
						.attr("height", (d) => Math.max(0, innerH - yScale(Number(d[s.dataKey]) || 0)));
				});
			}
		}

		// -----------------------------------------------------------------------
		// Horizontal layout
		// -----------------------------------------------------------------------
		if (isHorizontal) {
			const yScale = d3
				.scaleBand()
				.domain(data.map((d) => String(d[xDataKey])))
				.range([0, innerH])
				.padding(0.25);

			const xMax = stacked
				? d3.max(data, (d) => keys.reduce((acc, k) => acc + (Number(d[k]) || 0), 0)) ?? 0
				: d3.max(data, (d) => d3.max(keys, (k) => Number(d[k]) || 0) ?? 0) ?? 0;

			const xScale = d3.scaleLinear().domain([0, xMax * 1.1]).nice().range([0, innerW]);

			// Grid lines
			g.append("g")
				.attr("class", "chart-grid")
				.call(
					d3.axisBottom(xScale)
						.tickSize(innerH)
						.tickFormat(() => "")
						.ticks(5)
				)
				.attr("transform", `translate(0,0)`)
				.select(".domain")
				.remove();

			g.selectAll(".chart-grid .tick line")
				.attr("stroke", "#e5e7eb")
				.attr("stroke-dasharray", "3,3");

			// Y axis
			g.append("g")
				.call(d3.axisLeft(yScale).tickSizeOuter(0))
				.selectAll("text")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "11px")
				.attr("fill", "#6b7280");

			// X axis
			g.append("g")
				.attr("transform", `translate(0,${innerH})`)
				.call(d3.axisBottom(xScale).ticks(5).tickFormat((v) => formatNumber(Number(v))))
				.selectAll("text")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "11px")
				.attr("fill", "#6b7280");

			g.selectAll(".domain").attr("stroke", "#e5e7eb");

			if (stacked) {
				const stack = d3.stack<Record<string, string | number>>().keys(keys);
				const stackedData = stack(data);

				stackedData.forEach((layer, i) => {
					const seriesName = series[i]?.name ?? keys[i];
					const colour = colourScale(i);

					g.selectAll(`.bar-h-layer-${i}`)
						.data(layer)
						.join("rect")
						.attr("class", `bar-h-layer-${i}`)
						.attr("y", (d) => yScale(String((d.data as Record<string, unknown>)[xDataKey])) ?? 0)
						.attr("height", yScale.bandwidth())
						.attr("fill", colour)
						.attr("rx", i === stackedData.length - 1 ? 3 : 0)
						.attr("x", 0)
						.attr("width", 0)
						.on("mouseenter", function (event: MouseEvent, d) {
							const value = d[1] - d[0];
							if (tooltipRef.current) {
								tooltipRef.current.innerHTML = `<strong>${seriesName}</strong><br/>${formatNumber(value)}`;
								tooltipRef.current.style.opacity = "1";
								positionTooltip(tooltipRef.current, event);
							}
							d3.select(this).attr("opacity", 0.8);
						})
						.on("mousemove", function (event: MouseEvent) {
							if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
						})
						.on("mouseleave", function () {
							if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
							d3.select(this).attr("opacity", 1);
						})
						.transition()
						.duration(600)
						.delay((_, j) => j * 20)
						.ease(d3.easeCubicOut)
						.attr("x", (d) => xScale(d[0]))
						.attr("width", (d) => Math.max(0, xScale(d[1]) - xScale(d[0])));
				});
			} else {
				const yGroupScale = d3
					.scaleBand()
					.domain(keys)
					.range([0, yScale.bandwidth()])
					.padding(0.05);

				series.forEach((s, i) => {
					const colour = colourScale(i);

					g.selectAll(`.bar-h-group-${i}`)
						.data(data)
						.join("rect")
						.attr("class", `bar-h-group-${i}`)
						.attr("y", (d) => (yScale(String(d[xDataKey])) ?? 0) + (yGroupScale(s.dataKey) ?? 0))
						.attr("height", yGroupScale.bandwidth())
						.attr("fill", colour)
						.attr("rx", 3)
						.attr("x", 0)
						.attr("width", 0)
						.on("mouseenter", function (event: MouseEvent, d) {
							const value = Number(d[s.dataKey]) || 0;
							if (tooltipRef.current) {
								tooltipRef.current.innerHTML = `<strong>${s.name}</strong><br/>${formatNumber(value)}`;
								tooltipRef.current.style.opacity = "1";
								positionTooltip(tooltipRef.current, event);
							}
							d3.select(this).attr("opacity", 0.8);
						})
						.on("mousemove", function (event: MouseEvent) {
							if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
						})
						.on("mouseleave", function () {
							if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
							d3.select(this).attr("opacity", 1);
						})
						.transition()
						.duration(600)
						.delay((_, j) => j * 20)
						.ease(d3.easeCubicOut)
						.attr("width", (d) => Math.max(0, xScale(Number(d[s.dataKey]) || 0)));
				});
			}
		}
	}, [data, series, xDataKey, xAxisLabel, yAxisLabel, layout, stacked, containerWidth, containerHeight, isModal]);

	useEffect(() => {
		renderChart();
	}, [renderChart]);

	if (!data || data.length === 0) {
		return (
			<div className="chart-empty-state" role="status">
				No data available
			</div>
		);
	}

	return (
		<div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
			{/* height: 0 prevents the browser default 150px SVG height before D3 renders.
			     D3's renderChart sets .style("height") to override this once dimensions are known. */}
			<svg ref={svgRef} style={{ display: "block", width: "100%", height: 0 }} />

			{/* Legend */}
			{series.length > 1 && (
				<div className="chart-legend" aria-label="Chart legend">
					{series.map((s, i) => (
						<div key={s.dataKey} className="chart-legend-item">
							<span
								className="chart-legend-swatch"
								style={{ background: s.colour || SERIES_COLOURS[i % SERIES_COLOURS.length] }}
								aria-hidden="true"
							/>
							<span className="chart-legend-label">{s.name}</span>
						</div>
					))}
				</div>
			)}

			{/* Tooltip */}
			<div
				ref={tooltipRef}
				className="chart-tooltip"
				role="tooltip"
				aria-live="polite"
				style={{ opacity: 0 }}
			/>
		</div>
	);
}
